import { useEffect, useState } from "react";
import { Box, Flex, Text, Stack, Image, Spinner, Icon } from "@chakra-ui/react";
import { Camera } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { displayAnswer } from "@/components/FieldInput";
import { colors } from "@/theme/colors";

export function PhotoAnswer({ photos, fieldId }) {
  const [urls, setUrls] = useState({});
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const withPath = photos.filter((p) => p.path);
        // Time-limited signed URLs — photos are never publicly reachable.
        const results = await Promise.all(withPath.map((p) =>
          supabase.storage.from("submission-photos").createSignedUrl(p.path, 300)));
        const out = {};
        withPath.forEach((p, i) => { if (results[i].data?.signedUrl) out[p.path] = results[i].data.signedUrl; });
        setUrls(out);
        if (!Object.keys(out).length && withPath.length) setFailed(true);
      } catch { setFailed(true); } // offline: signed-URL fetch throws — show placeholder, not spinner
    })();
  }, [photos]);
  return (
    <Flex gap="2" flexWrap="wrap">
      {photos.map((p, i) => urls[p.path] ? (
        <a key={p.path || p.id || i} href={urls[p.path]} target="_blank" rel="noreferrer" data-testid={`photo-link-${fieldId}-${i}`}>
          <Image src={urls[p.path]} alt={p.name || "Photo"} boxSize="110px" objectFit="cover"
            borderRadius="10px" border="1px solid" borderColor={colors.border} />
        </a>
      ) : failed ? (
        <Flex key={p.path || p.id || i} boxSize="110px" direction="column" align="center" justify="center" gap="1"
          borderRadius="10px" bg={colors.cream} border="1px solid" borderColor={colors.border} px="2"
          data-testid={`photo-offline-${fieldId}-${i}`}>
          <Icon as={Camera} boxSize="4" color={colors.textSecondary} />
          <Text fontSize="10px" color={colors.textSecondary} textAlign="center">Photo needs a connection</Text>
        </Flex>
      ) : (
        <Flex key={p.path || p.id || i} boxSize="110px" align="center" justify="center" borderRadius="10px" bg={colors.cream}>
          <Spinner size="sm" color={colors.teal} />
        </Flex>
      ))}
    </Flex>
  );
}

export const AnswerValue = ({ field, value }) => {
  if (field.type === "photo") {
    return Array.isArray(value) && value.length > 0
      ? <PhotoAnswer photos={value} fieldId={field.id} />
      : <Text fontSize="sm" color={colors.textSecondary}>—</Text>;
  }
  if (field.type === "signature") {
    return typeof value === "string" && value.startsWith("data:image")
      ? <Image src={value} alt="Signature" maxW="260px" bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="10px" data-testid={`signature-img-${field.id}`} />
      : <Text fontSize="sm" color={colors.textSecondary}>—</Text>;
  }
  return <Text fontSize="sm" fontWeight="500" color={colors.textPrimary} whiteSpace="pre-wrap">{displayAnswer(field, value)}</Text>;
};

export const AnswersSections = ({ sections, sub, reviewPill = () => null, qcBoxIfOpen = () => null }) => (
  <Stack gap="4" mb="4">
    {sections.map((sec, si) => {
      const scope = { sectionId: sec.id, sectionTitle: sec.title || `Section ${si + 1}` };
      return (
        <Box key={sec.id || si} bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" data-testid={`detail-section-${si}`}>
          <Flex justify="space-between" align="center" gap="3" mb="3">
            <Text fontWeight="700" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em" color={colors.tealHover}>
              {scope.sectionTitle}
            </Text>
            {reviewPill(scope)}
          </Flex>
          {qcBoxIfOpen(scope) && <Box mb="3">{qcBoxIfOpen(scope)}</Box>}
          <Stack gap="4">
            {(sec.fields || []).map((fl) => fl.type === "info" ? (
              <Box key={fl.id} px="3.5" py="2.5" borderRadius="10px" bg={colors.tealTint} borderLeft="3px solid" borderColor={colors.teal}>
                <Text fontSize="sm" color={colors.textPrimary}>{fl.label}</Text>
              </Box>
            ) : (
              <Box key={fl.id} data-testid={`detail-field-${fl.id}`}>
                <Text fontSize="sm" color={colors.textSecondary} mb="1">{fl.label}</Text>
                <AnswerValue field={fl} value={sub.answers?.[fl.id]} />
              </Box>
            ))}
          </Stack>
        </Box>
      );
    })}
  </Stack>
);
